import * as React from "react";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    ThemeProvider,
    Typography,
} from "@mui/material";
import { t } from "i18next";
import { PalletPickupFormCarrierCountryDeclarationInput } from "./pallet-pickup-form-carrier-country-declaration-input";
import { DialogMessageIcon, dialogModel } from "../../../common/component/dialog/dialog-model";
import { PalletPickupFormCarrierCountryDeclarationInputGroup } from "./pallet-pickup-form-carrier-country-declaration-input-group";
import { PalletPickupFormComments } from "./pallet-pickup-form-comments";
import { VPUITheme } from "../../../common/theme/vpui-theme";
import { Close } from "@mui/icons-material";
import { DialogActionsWithOption } from "../../../common/component/dialog-actions-with-option/dialog-actions-with-option";
import { DialogActionsOption } from "../../../common/component/dialog-actions-with-option/dialog-actions-option";
import { DialogActionsButtons } from "../../../common/component/dialog-actions-with-option/dialog-actions-buttons";

import "./style.scss";
import { context } from "../../../context/context";
import { Permission } from "../../../context/permission";
import { CarrierCountryPalletDeclarationFormModel } from "./carrier-country-pallet-declaration-input-model";
import * as moment from "moment/moment";
import { palletPickupService } from "../../services/pallet-pickup-service";
import { PalletReportFormStatus } from "../../model/pallet-report-form";
import { uniq } from "lodash";

interface IPalletPickupFormDialogProps {
    openOnStartup: boolean;
    onDialogClose: () => void;
}

interface IPalletPickupFormDialogState {
    declarationElements: CarrierCountryPalletDeclarationFormModel[];
    creationComment: string;
    closingComment: string;
    allowSubmission: boolean;
    deadline: moment.Moment;
    isLoading: boolean;
}

export class PalletPickupFormDialog extends React.Component<IPalletPickupFormDialogProps, IPalletPickupFormDialogState> {
    constructor(props: IPalletPickupFormDialogProps) {
        super(props);

        this.state = {
            declarationElements: [],
            creationComment: "",
            closingComment: "",
            allowSubmission: true,
            deadline: moment(),
            isLoading: true,
        };
    }

    public async componentDidMount() {
        if (!context.isSupplier) {
            return;
        }

        const palletForm = await palletPickupService.getPalletForm();
        if (palletForm.carrierCountryPalletDeclarations.length === 0) {
            if (!this.props.openOnStartup) {
                dialogModel.alert(
                    t("palletPickup.declarationForm.title"),
                    t("palletPickup.declarationForm.errorFormNotAvailable"),
                    DialogMessageIcon.Error,
                );
            }

            this.props.onDialogClose();
            return;
        }

        if (palletForm.status !== PalletReportFormStatus.Modified && this.props.openOnStartup) {
            this.props.onDialogClose();
            return;
        }

        this.setState({
            declarationElements: palletForm.carrierCountryPalletDeclarations
                .map(declaration => new CarrierCountryPalletDeclarationFormModel(declaration)),
            allowSubmission: palletForm.status !== PalletReportFormStatus.Locked,
            deadline: palletForm.deadline,
            closingComment: palletForm.palletClosingComments,
            creationComment: palletForm.palletCreationComments,
            isLoading: false,
        });
    }

    public render() {
        return <Dialog
            open={!this.state.isLoading}
            onClose={this.props.onDialogClose}
            className={"pallet-pickup-form"}
            data-testid={"pallet-pickup-form"}
        >
            <ThemeProvider theme={VPUITheme}>
                <div className={"pallet-pickup-form__container"}>
                    <IconButton onClick={this.props.onDialogClose}
                                className="pallet-pickup-form__close-button"
                                data-testid={"pallet-pickup-form-close"}>
                        <Close />
                    </IconButton>
                    <DialogTitle>
                        {t("palletPickup.declarationForm.title")}
                    </DialogTitle>
                    <DialogContent>
                        <Typography className={"pallet-pickup-form__description"}>
                            {t("palletPickup.declarationForm.description")}
                        </Typography>
                        {this.state.allowSubmission && <Typography className={"pallet-pickup-form__description"}>
                            {this.deadlineMessage}
                        </Typography>}
                        {this.declarationCountryList.map(country =>
                            <PalletPickupFormCarrierCountryDeclarationInputGroup
                                key={country}
                                name={t(`palletPickup.declarationForm.country.${country}`)}>
                                {this.declarationElementForCountry(country).map(item =>
                                        <PalletPickupFormCarrierCountryDeclarationInput
                                            model={item}
                                            isReadOnly={!this.state.allowSubmission}
                                            key={`${item.country}-${item.carrier}`}
                                            onModelChange={model => this.onDeclarationChange(item, model)}
                                        />)}
                            </PalletPickupFormCarrierCountryDeclarationInputGroup>)}
                        <PalletPickupFormComments closingCommentValue={this.state.closingComment}
                                                  onClosingCommentUpdate={value => this.setState({closingComment: value})}
                                                  withCreationComment={context.hasPermission(Permission.FeaturePalletWorkflow)}
                                                  creationCommentValue={this.state.creationComment}
                                                  onCreationCommentUpdate={value => this.setState({ creationComment: value })}
                                                  isReadOnly={!this.state.allowSubmission}/>
                    </DialogContent>
                    <DialogActionsWithOption>
                        <DialogActionsOption>
                            <div className={"pallet-pickup-form-footer__error-note"}>
                                {!this.isFormValidated && <Typography className={"pallet-pickup-form-footer__error-note-text"}>
                                    {t("palletPickup.declarationForm.errorsDetected")}
                                </Typography>}
                                {!this.state.allowSubmission && <Typography className={"pallet-pickup-form-footer__error-note-text"}>
                                    {t("palletPickup.declarationForm.formAlreadySubmitted")}
                                </Typography>}
                            </div>
                        </DialogActionsOption>
                        <DialogActionsButtons>
                            <Button
                                color={"primary"}
                                variant={"contained"}
                                type={"submit"}
                                className={"pallet-pickup-form-footer__button"}
                                data-testid={"pallet-pickup-form-submit-button"}
                                disabled={!this.isFormValidated || !this.state.allowSubmission}
                                onClick={() => this.executeSubmit()}
                            >
                                {t("palletPickup.declarationForm.submit.action")}
                            </Button>
                        </DialogActionsButtons>
                    </DialogActionsWithOption>
                </div>
            </ThemeProvider>
        </Dialog>;
    }

    private get isFormValidated(): boolean {
        return this.state.declarationElements.every(element => element.isValid);
    }

    private get isCompletelyFulfilled(): boolean {
        return this.state.declarationElements.every(element => element.palletCount !== "0");
    }

    private get isDeadlineToday(): boolean {
        return this.state.deadline.isSame(moment(), "day");
    }

    private get deadlineDateFormatted(): string {
        return this.state.deadline.format("d.m.Y");
    }

    private get deadlineTimeFormatted(): string {
        return this.state.deadline.format("H:mm");
    }

    private get declarationCountryList(): string[] {
        return uniq(this.state.declarationElements
            .map(e => e.country));
    }

    private onDeclarationChange(item: CarrierCountryPalletDeclarationFormModel, model: CarrierCountryPalletDeclarationFormModel) {
        this.setState({
            declarationElements: this.state.declarationElements.map(element =>
                    element === item ? model : element,
            ),
        });
    }

    private executeSubmit() {
        dialogModel.confirmBox(
            t("palletPickup.declarationForm.submit.title"),
            this.isCompletelyFulfilled ? t("palletPickup.declarationForm.submit.description") : t("palletPickup.declarationForm.submit.descriptionWithIncompleteFormWarning"),
            () => this.submitFormAndClose(),
        );
    }

    private async submitFormAndClose() {
        await palletPickupService
            .savePalletForm(this.state.declarationElements.map(d => d.toDataModel()), this.state.closingComment, this.state.creationComment);
        this.props.onDialogClose();
    }

    private get deadlineMessage(): string {
        return this.isDeadlineToday ? this.deadlineMessageForToday : this.deadlineMessageGeneric;
    }

    private get deadlineMessageForToday() {
        return t(`palletPickup.declarationForm.deadlineToday`, {
            deadlineTime: this.deadlineTimeFormatted,
        });
    }

    private get deadlineMessageGeneric() {
        return t(`palletPickup.declarationForm.deadline`, {
            deadlineDate: this.deadlineDateFormatted,
            deadlineTime: this.deadlineTimeFormatted,
        });
    }

    private declarationElementForCountry(country: string) {
        return this.state.declarationElements.filter(item => item.country === country);
    }
}
